/* eslint-disable no-use-before-define */

import {
  AffectedPerson,
  ContactInterestType,
  ExperienceExchangeTopic,
  FoundingInterest,
  GroupStatus,
  HeadQuarterType,
  InterestedIn,
  Language,
  MeetingFormat,
  Organization,
  OrganizationContactPerson,
  OrganizationEmail,
  OrganizationSubType,
  OrganizationSubTypeForm,
  OrganizationTopic,
  PhoneContact,
  File as ProtoFile,
  TargetGroup, Topic,
} from '@common/entities';
import { OrganizationType, Status } from '@lib/types';
import { TopicModel } from 'src/app/topic/@shared/topic.model';
import { getColorByFormType } from 'src/app/@shared/form/form-helper';

export type OrganizationSource = Organization;
export type OrganizationContactPersonSource = OrganizationContactPerson;

// Add attribute pairs here, which correlate between model and entity but have different names
// e.g. emails and emailsExtended - e.g. the clipboard uses form config (model values) for visualization
// but also processes entity values from the public-fields config
export const MODEL_TO_ENTITY_FIELD_KEY_MAP: { [modelField in keyof OrganizationModel]?: keyof Organization } = {
  emailsExtended: 'emails',
};

type OrganizationFreeTextKey = keyof Organization | 'providedByRed' | 'providedByOther';

// Define the keys which should have additional free text fields only here!
export const ORGANIZATION_KEYS_WITH_FREE_TEXT
  = [
    'topics',
    'contactPersons',
    'providedByOther',
    'contactOrganization',
    'provider',
  ] as const satisfies readonly OrganizationFreeTextKey[];

export const FORM_KEY_TO_FREE_TEXT_KEY_MAP = {
  contactOrganization: 'contactOrganizationFreeText',
  contactPersons: 'contactPersonsFreeText',
  providedByOther: 'providedByOtherFreeText',
  provider: 'providerFreeText',
  topics: 'topicsFreeText',
} as const satisfies { [K in typeof ORGANIZATION_KEYS_WITH_FREE_TEXT[number]]: string };

export type FreeTextFormFieldKey = typeof FORM_KEY_TO_FREE_TEXT_KEY_MAP[keyof typeof FORM_KEY_TO_FREE_TEXT_KEY_MAP];
export const FREE_TEXT_FORM_KEYS = Object.values(FORM_KEY_TO_FREE_TEXT_KEY_MAP) as FreeTextFormFieldKey[];
export type LatestDataReviewSummary = OrganizationSource['latestDataReview'];

export class OrganizationModel {
  public createdAt: string;
  public updatedAt: string;
  public id: string;
  public legacyId?: string;
  public legacyTopics?: string[];
  public type: OrganizationType;
  public subType?: OrganizationSubType;
  public subTypeForm?: OrganizationSubTypeForm;
  public searchForms?: OrganizationSubTypeForm[];

  public status?: Status;
  public isPublished?: boolean;
  public deletionReason?: string;
  public name: string;
  public fileId: string;
  public file: ProtoFile;
  public source: string;
  public shortName?: string;
  public addressCo?: string;
  public addressStreetAndNumber?: string;
  public addressPostalCode?: string;
  public addressCity?: string;
  public meetingPostalCode?: string;
  public meetingLocation?: string;
  public addressFederalState?: string;
  public contactViaNakos?: boolean;
  public phoneContacts?: PhoneContact[];
  public emails: string[];
  public emailsExtended: OrganizationEmail[];
  public websites?: string[];
  public socialMediaLinks?: string[];
  public contactPersons?: OrganizationContactPersonSource[];
  public notes?: string;
  public foundingYear?: number;
  public workingMethod?: string;
  public isMainOffice?: boolean;
  public mainOffice?: OrganizationModel;
  public branchOffices?: OrganizationModel[];
  public coverageArea?: string;
  public requestsReceivedLastYear?: string;
  public numberOfCurrentlyAffiliatedGroups?: number;
  public providesFor?: OrganizationModel[];
  public providedByRed?: OrganizationModel | null; // providedBy entries are splitted into red and other addresses for easier form handling
  public providedByOther?: OrganizationModel | null; // providedBy entries are splitted into red and other addresses for easier form handling
  public isBarrierFree?: boolean;
  public qualifications?: string[];
  public media?: string[];
  public topics?: TopicModel[];
  public additionalTopics?: string;
  public memberCount?: number;
  public headquarters?: HeadQuarterType;
  public stateLevelSubdivisions?: string[];

  public hasTransparencyLabel?: boolean;
  public transparencyPdfId?: string;
  public transparencyPdf?: ProtoFile;

  public groupStatus?: GroupStatus;
  public targetGroup?: TargetGroup[];
  public description?: string;
  public meetingFormat?: MeetingFormat[];
  public toolUsed?: string;
  public dataProtectionCompliant?: boolean;
  public language?: Language;
  public problemExplanation?: string;
  public specificIssues?: string;
  public affectedPerson?: AffectedPerson;
  public dateOfBirth?: string;
  public interestedInfos?: InterestedIn[];
  public experienceExchangeTopics?: ExperienceExchangeTopic[];
  public foundingInterests?: FoundingInterest[];
  public contactInterests?: ContactInterestType[];
  public localContactPoint?: OrganizationModel;
  public contactOrganization?: OrganizationModel;
  public provider?: OrganizationModel;

  public indicatorColor: string;
  public displayName: string;
  public latestDataReview?: LatestDataReviewSummary;

  // eslint-disable-next-line max-statements,max-lines-per-function
  constructor(source: OrganizationSource) {
    this.createdAt = source.createdAt;
    this.updatedAt = source.updatedAt;
    this.id = source.id;
    this.legacyId = source.legacyId;
    this.legacyTopics = source.legacyTopics || [];
    this.type = source.type;
    this.subType = source.subType;
    this.subTypeForm = source.subTypeForm;
    this.searchForms = source.searchForms || [];
    this.source = source.source;

    this.status = source.status;
    this.isPublished = source.isPublished;
    this.deletionReason = (source as OrganizationSource & { deletionReason?: string }).deletionReason;
    this.name = source.name;
    this.shortName = source.shortName;
    this.addressCo = source.addressCo;
    this.addressStreetAndNumber = source.addressStreetAndNumber;
    this.addressPostalCode = source.addressPostalCode;
    this.addressCity = source.addressCity;
    this.meetingPostalCode = source.meetingPostalCode;
    this.meetingLocation = source.meetingLocation;
    this.addressFederalState = source.addressFederalState;
    this.contactViaNakos = source.contactViaNakos;
    this.phoneContacts = this.getValidPhoneContacts(source.phoneContacts);
    this.emailsExtended = this.getEmailsExtended(source.emails);
    this.emails = this.getValidEmails(source.emails);
    this.websites = source.websites || [];
    this.socialMediaLinks = source.socialMediaLinks || [];
    this.contactPersons = this.isArrayValid(source.contactPersons)
      ? source.contactPersons
      : [];
    this.notes = source.notes;
    this.foundingYear = source.foundingYear;
    this.workingMethod = source.workingMethod;
    this.isMainOffice = source.isMainOffice;
    this.memberCount = source.memberCount;
    this.headquarters = source.headquarters;
    this.stateLevelSubdivisions = source.stateLevelSubdivisions || [];

    this.hasTransparencyLabel = source.hasTransparencyLabel;
    this.transparencyPdfId = source.transparencyPdfId;
    this.transparencyPdf = source.transparencyPdf;

    this.groupStatus = source.groupStatus;
    this.targetGroup = source.targetGroup;
    this.description = source.description;
    this.meetingFormat = source.meetingFormat;
    this.toolUsed = source.toolUsed;
    this.dataProtectionCompliant = source.dataProtectionCompliant;
    this.language = source.language;
    this.problemExplanation = source.problemExplanation;
    this.specificIssues = source.specificIssues;
    this.affectedPerson = source.affectedPerson;
    this.dateOfBirth = source.dateOfBirth;
    this.interestedInfos = source.interestedInfos || [];
    this.experienceExchangeTopics = source.experienceExchangeTopics || [];
    this.foundingInterests = source.foundingInterests || [];
    this.contactInterests = source.contactInterests || [];

    this.mainOffice = source.mainOffice
      ? new OrganizationModel(source.mainOffice)
      : null;
    this.branchOffices = this.getSortedOrganizations(source.branchOffices);

    this.coverageArea = source.coverageArea;
    this.requestsReceivedLastYear = source.requestsReceivedLastYear;
    this.numberOfCurrentlyAffiliatedGroups = source.numberOfCurrentlyAffiliatedGroups;
    this.providesFor = this.getSortedOrganizations(source.providesFor);

    this.setProvidedByPerType(source.providedBy);

    this.isBarrierFree = source.isBarrierFree;
    this.qualifications = source.qualifications || [];
    this.media = source.media || [];
    this.topics = this.getTopics(source.topics);
    this.additionalTopics = source.additionalTopics;

    this.fileId = source.fileId;
    this.file = source.file;

    this.localContactPoint = source.localContactPoint
      ? new OrganizationModel(source.localContactPoint)
      : null;
    this.contactOrganization = source.contactOrganization
      ? new OrganizationModel(source.contactOrganization)
      : null;
    this.provider = source.provider
      ? new OrganizationModel(source.provider)
      : null;

    this.displayName = source.name || source.id;
    this.indicatorColor = getColorByFormType(source.type, source.subType);

    if (Object.prototype.hasOwnProperty.call(source, 'latestDataReview')) {
      this.latestDataReview = source.latestDataReview;
    }
  }

  private getValidPhoneContacts(source: PhoneContact[]): PhoneContact[] {
    if (!this.isArrayValid(source)) {
      return [];
    }

    return source;
  }

  // eslint-disable-next-line class-methods-use-this
  private getEmailsExtended(emails: OrganizationEmail[]): OrganizationEmail[] {
    if (!emails || !emails.length) {
      return [];
    }

    // public api calls can return flat string email arrays, but for consistent frontend handling we reformat here
    const isFlatArray = emails.some((email) => {
      return typeof email !== 'object';
    });

    if (isFlatArray) {
      return emails.map((email) => {
        if (typeof email !== 'object') {
          return {
            email,
          };
        }

        return email;
      });
    }

    return emails;
  }

  private getValidEmails(emails: (OrganizationEmail | string)[]): string[] {
    return emails?.map((e) => {
      if (typeof e === 'string') {
        return e;
      }

      return e.email;
    }).filter((email) => {
      return !!email;
    }) || [];
  }

  private getSortedOrganizations(source: Organization[]): OrganizationModel[] {
    if (!this.isArrayValid(source)) {
      return [];
    }

    const items = source.map((item) => {
      return new OrganizationModel(item);
    });

    return items.sort((a, b) => {
      if (a.displayName < b.displayName) {
        return -1;
      } else if (a.displayName === b.displayName) {
        return 0;
      }

      return 1;
    });
  }

  private getTopics(source: Array<OrganizationTopic | Topic>): TopicModel[] {
    if (!this.isArrayValid(source)) {
      return [];
    }

    return source
      .map((topicOrRelation) => {
        if ('topic' in topicOrRelation) {
          return topicOrRelation.topic;
        }

        return topicOrRelation;
      })
      .filter((topic): topic is Topic => {
        return !!topic;
      })
      .map((topic) => {
        return new TopicModel(topic);
      });
  }

  private setProvidedByPerType(providedBy: Organization): void {
    if (!providedBy) {
      this.providedByRed = null;
      this.providedByOther = null;

      return;
    }

    if (providedBy.subType === 'selfHelpAddressRa') {
      this.providedByRed = new OrganizationModel(providedBy);
      this.providedByOther = null;
    } else {
      this.providedByRed = null;
      this.providedByOther = new OrganizationModel(providedBy);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private isArrayValid(source: unknown): boolean {
    return Array.isArray(source)
      && source.every((item) => {
        return typeof item === 'object'
          && item !== null && Object.keys(item).length > 0;
      });
  }
}
